import { createClient } from '@supabase/supabase-js'
import type { EventMetadataMap } from '@important-not-important-projects/wcid-types'
import { isInBrowser } from '../../utilities/browser'

// Event types a browser may submit directly to track_event with the anon key.
// These mirror the `is_client_safe` rows in the Supabase event_types registry —
// server-only types (user_followed, action_potential_credited, geo_context_alert,
// etc.) are rejected by track_event's forge-guard and must go through a server
// (service-role) path instead.
type ClientSafeEventType =
  | 'action_taken'
  | 'action_removed'
  | 'action_untaken'
  | 'action_favorited'
  | 'action_unfavorited'
type OrganizationAggregateRow = {
  organization_key: string
  active_taker_count: number
}
type PositionAggregateRow = {
  position_slug: string
  active_taker_count: number
}
type PositionTakerRow = {
  user_id: string
  display_name: string | null
  avatar_url: string | null
  taken_at: string
}
export type ActionTakerDetailRow = {
  user_id: string
  display_name: string | null
  avatar_url: string | null
  taken_at: string
}
type ActionTakerRow = {
  action_slug: string
  user_id: string
}
type OrganizationAggregateChangeHandler = (row: OrganizationAggregateRow) => void
type PositionAggregateChangeHandler = (row: PositionAggregateRow) => void
type ActionTakerChangeHandler = (row: ActionTakerRow) => void

type TrackEventPayload<T extends ClientSafeEventType = ClientSafeEventType> = {
  eventType: T
  userId: string
  actionSlug: string
  occurredAt?: string
  // metadata is typed to the event's payload schema in @wcid/types
  // (EventMetadataMap), which the DB enforces via pg_jsonschema. Sending an
  // undeclared key or wrong type is a compile error here and a 400 at the DB.
  metadata?: EventMetadataMap[T]
}

const DEFAULT_LOCAL_SUPABASE_URL = 'http://127.0.0.1:54321'
const DEFAULT_LOCAL_SUPABASE_ANON_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZS1kZW1vIiwicm9sZSI6ImFub24iLCJleHAiOjE5ODM4MTI5OTZ9.CRXP1A7WOeoJeXxjNni43kdQwgnWNReilDMblYTn_I0'

const parsePackedGatsbySupabaseVars = () => {
  const [prodUrl, prodKey, developUrl, developKey] = (process.env.GATSBY_SUPABASE_VARS || '')
    .split('|')
    .map(entry => entry.trim())

  return {
    developKey: developKey || undefined,
    developUrl: developUrl || undefined,
    prodKey: prodKey || undefined,
    prodUrl: prodUrl || undefined
  }
}

const readPackedPair = ({
  key,
  target,
  url
}: {
  key?: string
  target: 'develop' | 'production'
  url?: string
}) => {
  if (!url && !key) {
    return null
  }

  if (!url || !key) {
    throw new Error(
      `GATSBY_SUPABASE_VARS ${target} configuration is incomplete. Expected ` +
        `"production_url|production_anon_key|develop_url|develop_anon_key".`
    )
  }

  return { key, url }
}

const decodeJwtPayload = (token: string) => {
  const parts = token.split('.')
  if (parts.length !== 3) return null

  try {
    const payload = parts[1]
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')
    return JSON.parse(atob(padded)) as { role?: string }
  } catch {
    return null
  }
}

const isServiceRoleKey = (token?: string) => decodeJwtPayload(token || '')?.role === 'service_role'

const isLocalSupabaseUrl = (url: string) => {
  try {
    const parsed = new URL(url)
    return ['127.0.0.1', 'localhost'].includes(parsed.hostname)
  } catch {
    return false
  }
}

type RuntimeTarget = 'local' | 'develop' | 'production'

const normalizeRuntimeTarget = (value?: string | null): RuntimeTarget | null => {
  const normalized = value?.trim().toLowerCase()

  switch (normalized) {
    case 'local':
      return 'local'
    case 'develop':
    case 'development':
    case 'dev':
      return 'develop'
    case 'production':
    case 'prod':
      return 'production'
    default:
      return null
  }
}

const normalizeBranchName = (value?: string | null) => value?.trim().toLowerCase() || null

const hasBranchName = (expected: string) =>
  [process.env.HEAD, process.env.BRANCH]
    .map(normalizeBranchName)
    .some(branchName => branchName === expected)

const getBrowserHostname = () => {
  if (!isInBrowser()) {
    return null
  }

  try {
    return new URL(window.location.href).hostname.toLowerCase()
  } catch {
    return null
  }
}

const getHostedRuntimeTargetHint = (): RuntimeTarget | null => {
  const hostname = getBrowserHostname()

  if (!hostname) {
    return null
  }

  if (
    hostname === 'whatcanido.earth' ||
    hostname === 'www.whatcanido.earth' ||
    hostname === 'staging.whatcanido.earth' ||
    hostname.startsWith('staging--')
  ) {
    return 'production'
  }

  if (hostname === 'develop.whatcanido.earth' || hostname.startsWith('develop--')) {
    return 'develop'
  }

  return null
}

const getRuntimeTarget = (): RuntimeTarget => {
  const explicitTarget = normalizeRuntimeTarget(process.env.GATSBY_SUPABASE_TARGET)
  if (explicitTarget) {
    return explicitTarget
  }

  const deployContext = process.env.CONTEXT || process.env.NETLIFY_DEPLOY_CONTEXT

  if (process.env.NETLIFY_DEV === 'true' || deployContext === 'dev') {
    return 'local'
  }

  if (!process.env.NETLIFY && process.env.NODE_ENV === 'development') {
    return 'local'
  }

  const isStagingBranch = hasBranchName('staging')
  const isDevelopBranch = hasBranchName('develop') || hasBranchName('development')

  if (deployContext === 'deploy-preview') {
    return 'develop'
  }

  if (deployContext === 'branch-deploy') {
    if (isStagingBranch) {
      return 'production'
    }

    return 'develop'
  }

  if (isStagingBranch) {
    return 'production'
  }

  if (isDevelopBranch) {
    return 'develop'
  }

  const hostedTargetHint = getHostedRuntimeTargetHint()
  if (hostedTargetHint) {
    return hostedTargetHint
  }

  return 'production'
}

const getSupabaseConfigSource = () => {
  const packed = parsePackedGatsbySupabaseVars()
  const runtimeTarget = getRuntimeTarget()

  if (runtimeTarget === 'local' && process.env.GATSBY_SUPABASE_LOCAL_URL?.trim()) {
    return 'GATSBY_SUPABASE_LOCAL_URL'
  }

  if (runtimeTarget === 'local') {
    return 'supabase local defaults'
  }

  if (runtimeTarget === 'develop' && packed.developUrl) {
    return 'GATSBY_SUPABASE_VARS.develop'
  }

  if (packed.prodUrl) {
    return 'GATSBY_SUPABASE_VARS.prod'
  }

  return 'unconfigured'
}

const getSupabaseConfig = () => {
  const packed = parsePackedGatsbySupabaseVars()
  const runtimeTarget = getRuntimeTarget()
  const packedDevelop = readPackedPair({
    key: packed.developKey,
    target: 'develop',
    url: packed.developUrl
  })
  const packedProd = readPackedPair({
    key: packed.prodKey,
    target: 'production',
    url: packed.prodUrl
  })

  if (runtimeTarget === 'develop') {
    if (!packedDevelop) {
      throw new Error(
        'Hosted develop Supabase browser configuration is missing. Set the develop URL and anon/publishable key in GATSBY_SUPABASE_VARS.'
      )
    }

    if (isServiceRoleKey(packedDevelop.key)) {
      console.error(
        'Supabase frontend config is using a service role key for hosted develop. GATSBY_SUPABASE_VARS must contain public anon/publishable keys.'
      )
      return
    }

    return {
      anonKey: packedDevelop.key,
      restUrl: `${packedDevelop.url.replace(/\/$/, '')}/rest/v1`,
      rpcUrl: `${packedDevelop.url.replace(/\/$/, '')}/rest/v1/rpc/track_event`
    }
  }

  const url =
    (runtimeTarget === 'local' ? process.env.GATSBY_SUPABASE_LOCAL_URL : undefined) ||
    (runtimeTarget === 'local' ? DEFAULT_LOCAL_SUPABASE_URL : undefined) ||
    packedProd?.url
  const anonKey =
    (runtimeTarget === 'local' ? process.env.GATSBY_SUPABASE_LOCAL_ANON_KEY : undefined) ||
    (runtimeTarget === 'local' ? DEFAULT_LOCAL_SUPABASE_ANON_KEY : undefined) ||
    packedProd?.key

  if (!url || !anonKey) return

  if (isServiceRoleKey(anonKey)) {
    console.error(
      'Supabase frontend config is using a service role key. GATSBY_SUPABASE_VARS must contain public anon/publishable keys, not service role keys.'
    )
    return
  }

  return {
    anonKey,
    restUrl: `${url.replace(/\/$/, '')}/rest/v1`,
    rpcUrl: `${url.replace(/\/$/, '')}/rest/v1/rpc/track_event`
  }
}

let supabaseClient: ReturnType<typeof createClient> | undefined
let supabaseAuthClient: ReturnType<typeof createClient> | undefined
let hasLoggedSupabaseInstance = false

const logSupabaseInstance = () => {
  if (hasLoggedSupabaseInstance || !isInBrowser()) {
    return
  }

  hasLoggedSupabaseInstance = true

  const config = getSupabaseConfig()

  if (!config) {
    console.log('[supabase] development config unavailable')
    return
  }

  const instanceUrl = config.restUrl.replace(/\/rest\/v1$/, '')

  console.log('[supabase] connected instance', {
    target: getRuntimeTarget(),
    instance: isLocalSupabaseUrl(instanceUrl) ? 'local' : 'remote',
    source: getSupabaseConfigSource(),
    url: instanceUrl
  })
}

export const getSupabaseClient = () => {
  if (!isInBrowser()) return

  logSupabaseInstance()

  if (supabaseClient) return supabaseClient

  const config = getSupabaseConfig()
  if (!config) return

  supabaseClient = createClient(config.restUrl.replace(/\/rest\/v1$/, ''), config.anonKey, {
    auth: {
      autoRefreshToken: false,
      persistSession: false
    }
  })

  return supabaseClient
}

export const getSupabaseAuthClient = () => {
  if (!isInBrowser()) return

  logSupabaseInstance()

  if (supabaseAuthClient) return supabaseAuthClient

  const config = getSupabaseConfig()
  if (!config) return

  supabaseAuthClient = createClient(config.restUrl.replace(/\/rest\/v1$/, ''), config.anonKey, {
    auth: {
      autoRefreshToken: true,
      detectSessionInUrl: false,
      persistSession: true
    }
  })

  return supabaseAuthClient
}

logSupabaseInstance()

const getSupabaseHeaders = (anonKey: string) => ({
  'Content-Type': 'application/json',
  apikey: anonKey,
  Authorization: `Bearer ${anonKey}`
})

// Headers carrying the signed-in user's access token, for endpoints that are
// closed to anonymous callers. Null when there is no session.
const getSupabaseSessionHeaders = async (anonKey: string) => {
  const sessionResult = await getSupabaseAuthClient()?.auth.getSession()
  const accessToken = sessionResult?.data.session?.access_token
  if (!accessToken) return null

  return {
    'Content-Type': 'application/json',
    apikey: anonKey,
    Authorization: `Bearer ${accessToken}`
  }
}

export const trackSupabaseEvent = async <T extends ClientSafeEventType>({
  actionSlug,
  eventType,
  metadata,
  occurredAt,
  userId
}: TrackEventPayload<T>) => {
  if (!isInBrowser() || !userId || !actionSlug) return

  const config = getSupabaseConfig()
  if (!config) return

  const response = await fetch(config.rpcUrl, {
    method: 'POST',
    headers: getSupabaseHeaders(config.anonKey),
    body: JSON.stringify({
      p_event_type: eventType,
      p_user_id: userId,
      p_action_slug: actionSlug,
      ...(occurredAt ? { p_occurred_at: occurredAt } : {}),
      ...(metadata ? { p_metadata: metadata } : {})
    })
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(errorText || `Supabase track_event failed with status ${response.status}`)
  }

  return await response.json()
}

const buildInFilter = (values: string[]) => `(${values.map(value => `"${value}"`).join(',')})`

const removeSupabaseChannel = async (channelName: string) => {
  const client = getSupabaseClient()
  if (!client) return

  const channel = client.getChannels().find(existingChannel => existingChannel.topic === channelName)
  if (!channel) return

  await client.removeChannel(channel)
}

export const getOrganizationAggregateCounts = async (organizationKeys: string[]) => {
  if (!isInBrowser() || organizationKeys.length === 0) return {}

  const config = getSupabaseConfig()
  if (!config) return {}

  const uniqueKeys = Array.from(new Set(organizationKeys.filter(Boolean)))
  if (uniqueKeys.length === 0) return {}

  const url = new URL(`${config.restUrl}/organization_aggregates`)
  url.searchParams.set('select', 'organization_key,active_taker_count')
  url.searchParams.set('organization_key', `in.${buildInFilter(uniqueKeys)}`)

  const response = await fetch(url.toString(), {
    headers: getSupabaseHeaders(config.anonKey)
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(
      errorText || `Supabase organization_aggregates fetch failed with status ${response.status}`
    )
  }

  const rows = (await response.json()) as OrganizationAggregateRow[]
  return rows.reduce(
    (counts, row) => {
      counts[row.organization_key] = row.active_taker_count || 0
      return counts
    },
    {} as Record<string, number>
  )
}

export const getPositionAggregateCount = async (positionSlug: string) => {
  if (!isInBrowser() || !positionSlug) return 0

  const config = getSupabaseConfig()
  if (!config) return 0

  const url = new URL(`${config.restUrl}/position_aggregates`)
  url.searchParams.set('select', 'position_slug,active_taker_count')
  url.searchParams.set('position_slug', `eq.${positionSlug}`)
  url.searchParams.set('limit', '1')

  const response = await fetch(url.toString(), {
    headers: getSupabaseHeaders(config.anonKey)
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(
      errorText || `Supabase position_aggregates fetch failed with status ${response.status}`
    )
  }

  const rows = (await response.json()) as PositionAggregateRow[]
  return rows[0]?.active_taker_count || 0
}

export const getPositionTakers = async (positionSlug: string, limit = 24) => {
  if (!isInBrowser() || !positionSlug) return []

  const config = getSupabaseConfig()
  if (!config) return []

  // Who took a position is only shown to signed-in users.
  const headers = await getSupabaseSessionHeaders(config.anonKey)
  if (!headers) return []

  const response = await fetch(`${config.restUrl}/rpc/get_position_takers`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      p_position_slug: positionSlug,
      p_limit: limit
    })
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(errorText || `Supabase get_position_takers failed with status ${response.status}`)
  }

  return (await response.json()) as PositionTakerRow[]
}

export const getActionTakers = async (actionSlug: string, limit = 24) => {
  if (!isInBrowser() || !actionSlug) return []

  const config = getSupabaseConfig()
  if (!config) return []

  // Who took an action is only shown to signed-in users.
  const headers = await getSupabaseSessionHeaders(config.anonKey)
  if (!headers) return []

  const response = await fetch(`${config.restUrl}/rpc/get_action_takers`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      p_action_slug: actionSlug,
      p_limit: limit
    })
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(errorText || `Supabase get_action_takers failed with status ${response.status}`)
  }

  return (await response.json()) as ActionTakerDetailRow[]
}

export const subscribeToOrganizationAggregateCounts = (
  organizationKeys: string[],
  onChange: OrganizationAggregateChangeHandler
) => {
  const client = getSupabaseClient()
  const uniqueKeys = Array.from(new Set(organizationKeys.filter(Boolean)))

  if (!client || uniqueKeys.length === 0) {
    return () => null
  }

  const watchedKeys = new Set(uniqueKeys)
  const channelName = `apl-organization-aggregates:${uniqueKeys.sort().join(',')}`
  void removeSupabaseChannel(channelName)

  const channel = client
    .channel(channelName)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'organization_aggregates' },
      payload => {
        const row = (payload.new || payload.old) as OrganizationAggregateRow | undefined
        if (!row?.organization_key || !watchedKeys.has(row.organization_key)) return
        onChange({
          organization_key: row.organization_key,
          active_taker_count: row.active_taker_count || 0
        })
      }
    )
    .subscribe((status, error) => {
      if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        console.error('organization_aggregates subscription failed', status, error)
      }
    })

  return () => {
    void client.removeChannel(channel)
  }
}

export const subscribeToActionTakers = (
  actionSlugs: string[],
  onChange: ActionTakerChangeHandler
) => {
  const client = getSupabaseClient()
  const uniqueSlugs = Array.from(new Set(actionSlugs.filter(Boolean)))

  if (!client || uniqueSlugs.length === 0) {
    return () => null
  }

  const watchedSlugs = new Set(uniqueSlugs)
  const channelName = `apl-action-takers:${uniqueSlugs.sort().join(',')}`
  void removeSupabaseChannel(channelName)

  const channel = client
    .channel(channelName)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'action_takers' },
      payload => {
        const row = (payload.new || payload.old) as ActionTakerRow | undefined
        if (!row?.action_slug || !watchedSlugs.has(row.action_slug)) return
        onChange({
          action_slug: row.action_slug,
          user_id: row.user_id
        })
      }
    )
    .subscribe((status, error) => {
      if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        console.error('action_takers subscription failed', status, error)
      }
    })

  return () => {
    void client.removeChannel(channel)
  }
}

export const subscribeToPositionAggregateCount = (
  positionSlug: string,
  onChange: PositionAggregateChangeHandler
) => {
  const client = getSupabaseClient()

  if (!client || !positionSlug) {
    return () => null
  }

  const channelName = `apl-position-aggregates:${positionSlug}`
  void removeSupabaseChannel(channelName)

  const channel = client
    .channel(channelName)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'position_aggregates' },
      payload => {
        const row = (payload.new || payload.old) as PositionAggregateRow | undefined
        if (!row?.position_slug || row.position_slug !== positionSlug) return
        onChange({
          position_slug: row.position_slug,
          active_taker_count: row.active_taker_count || 0
        })
      }
    )
    .subscribe((status, error) => {
      if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        console.error('position_aggregates subscription failed', status, error)
      }
    })

  return () => {
    void client.removeChannel(channel)
  }
}
